import {lazy, Suspense} from 'react';
import {BackendClient, BackendClientContextProvider} from '@legit.health/backend-client';
import {createTheme} from '@legit.health/ui';
import {CssBaseline, GlobalStyles, Container, ThemeProvider, Alert} from '@mui/material';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ErrorBoundary} from 'react-error-boundary';
import {useTranslation} from 'react-i18next';
import AppSkeleton from './components/AppSkeleton';
import DiagnosticReportForm from './components/DiagnosticReportForm/DiagnosticReportForm';
import DiagnosticReportPdf from './components/DiagnosticReportPdf/DiagnosticReportPdf';
import AppErrorBoundary from './components/ErrorBoundary';
import {ParamsContextProvider} from './contexts/ParamsContext';
const TemporaryImagesUpload = lazy(() => import('./components/TemporaryImagesUpload'));

const searchParams = new URLSearchParams(window.location.search);
const companyApiKey = searchParams.get('company');
const queryClient = new QueryClient();
const backendClient = new BackendClient({
  backendUrl: import.meta.env.VITE_BACKEND_COMPANY_API_URL,
  localStorageKey: `${import.meta.env.VITE_APP_KEY_PREFIX}_IFRAME`,
  authTokens: {token: companyApiKey ?? '', refresh_token: ''}
});

export default function App() {
  const {t} = useTranslation(['app']);
  const params = new URLSearchParams(window.location.search);
  const primary = params.get('primary');
  const secondary = params.get('secondary');
  const fontFamily = params.get('fontFamily') ?? 'Roboto';
  const diagnosticReportId = params.get('diagnosticReportId');
  const isTemporaryImagesPath = window.location.pathname.includes('/temporary-images');

  const format = params.get('format');
  const isPdf = format === 'pdf';

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider
          theme={createTheme({
            primary: primary ? `#${primary}` : null,
            secondary: secondary ? `#${secondary}` : null,
            fontFamily
          })}
        >
          <CssBaseline />
          <GlobalStyles
            styles={{
              body: {backgroundColor: '#fff', minHeight: '100vh'},
              '.page': {pageBreakAfter: 'always'}
            }}
          />
          <Container maxWidth="lg" sx={{py: 4, bgcolor: 'common.white'}}>
            <Suspense fallback={<AppSkeleton />}>
              <BackendClientContextProvider backendClient={backendClient}>
                <ParamsContextProvider>
                  {diagnosticReportId ? (
                    <ErrorBoundary
                      fallback={<Alert severity="error">{t('app:diagnositcReportGetError')}</Alert>}
                    >
                      <DiagnosticReportPdf diagnosticReportId={diagnosticReportId} isPdf={isPdf} />
                    </ErrorBoundary>
                  ) : isTemporaryImagesPath ? (
                    <TemporaryImagesUpload />
                  ) : (
                    <DiagnosticReportForm />
                  )}
                </ParamsContextProvider>
              </BackendClientContextProvider>
            </Suspense>
          </Container>
        </ThemeProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
